import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowUpToLine } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import DragAndDropLoadImage from "../form-fields/DragAndDropLoadImage";
import { Subtitle, Title } from "../Text";
import { Button } from "../ui/button";
import { CustomDialogFooter } from "../ui/custom-dialog";
import { DialogHeader } from "../ui/dialog";
import { Form } from "../ui/form";
import { Thumbnail } from "../ui/tumbnail";

const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];

const formSchema = z.object({
  file: z.instanceof(File, { message: "form.error.required" }).refine(
    (file) => {
      return ACCEPTED_IMAGE_TYPES.includes(file.type);
    },
    {
      message: "form.error.fileNotSupported",
    },
  ),
});

export const ArtmatchLoadImage = ({
  onNextStep,
  onCancel,
}: {
  onCancel: () => void;
  onNextStep: (file: File) => void;
}) => {
  const { t } = useTranslation();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      file: undefined,
    },
    mode: "onChange",
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    onNextStep(values.file);
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogHeader className="px-6 pt-6">
          <div className="flex gap-4">
            <Thumbnail>
              <ArrowUpToLine className="text-gray-700 size-6" />
            </Thumbnail>
            <div className="flex flex-col w-full gap-2">
              <Title>{t("artmatch.loadImage.title")}</Title>
              <Subtitle>{t("artmatch.loadImage.subtitle")}</Subtitle>
            </div>
          </div>
        </DialogHeader>
        <div className="p-6">
          <DragAndDropLoadImage
            control={form.control}
            config={{ name: "file", type: "inputFile" }}
            getValues={form.getValues}
          />
        </div>
        <CustomDialogFooter>
          <Button
            type="button"
            onClick={() => {
              form.reset();
              onCancel();
            }}
            variant="secondary"
          >
            {t("common.cancel")}
          </Button>
          <Button disabled={!form.formState.isValid} type="submit">
            {t("artmatch.loadImage.submit")}
          </Button>
        </CustomDialogFooter>
      </form>
    </Form>
  );
};
